import { BrokerContactActions } from '@/components/ui/BrokerContactActions'
import { UserAvatar } from '@/components/ui/user-avatar'
import { cn } from '@/lib/utils'

// The pill inverts the avatar: brand fill, white initials.
const BROKER_FALLBACK = 'bg-brand-700 text-xs font-bold text-white'

interface BrokerIdentityPillProps {
  name: string
  /** Display form, e.g. "(404) 555-0182". */
  phone: string
  email: string
  /** Headshot URL — falls back to initials when omitted. */
  avatarUrl?: string
  /**
   * Dialable number for the `tel:` link. Defaults to stripping the display
   * phone, which is only right when that string is already a real number.
   */
  phoneE164?: string
  /** Icon-only call button, for widths where the written-out number doesn't fit. */
  hidePhoneNumber?: boolean
  /** Rendered after the contact actions, e.g. the note button. */
  action?: React.ReactNode
  className?: string
}

/**
 * Rounded "Matched broker" identity pill — avatar, name, and the contact actions.
 */
export function BrokerIdentityPill({
  name,
  phone,
  email,
  avatarUrl,
  phoneE164,
  hidePhoneNumber,
  action,
  className,
}: BrokerIdentityPillProps) {
  return (
    <div
      className={cn(
        'border-ink-100 flex items-center gap-4 rounded-full border bg-white/60 py-1.5 pr-3 pl-1.5',
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <UserAvatar
          name={name}
          src={avatarUrl}
          className="size-9 shrink-0"
          fallbackClassName={BROKER_FALLBACK}
        />
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="text-ink-400 text-[11px] font-medium tracking-wide uppercase">Matched broker</span>
          <span className="text-ink truncate text-sm font-bold">{name}</span>
        </div>
      </div>
      <BrokerContactActions
        name={name}
        phone={phone}
        email={email}
        phoneE164={phoneE164}
        hidePhoneNumber={hidePhoneNumber}
        action={action}
      />
    </div>
  )
}
